import { Card, CountBird, Wall } from '@league/ui';
import type { ReactNode } from 'react';

/**
 * What is left here after the design system moved to `@league/ui`: the auth shell, which is
 * specific to this app rather than shared with the public site.
 *
 * Everything else — Button, Input, FormField, StatusChip, Card, Table — comes from the package now,
 * so `apps/web` and `apps/site` cannot drift into looking like two products. Import from
 * `@league/ui` directly; this file is not a re-export barrel and should not become one.
 */
export { Button, FormField as Field, Input } from '@league/ui';

/**
 * `role="alert"` so a screen reader announces a failed sign-in. Without it the form appears to do
 * nothing, which is the single most common accessibility failure on a login page.
 */
export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      /*
       * `tint-rust`, not `rust` at 8%. An alpha-derived surface takes its final value from whatever
       * is behind it, so this note was a different pink on a card than on the page — and a colour
       * nobody chose is a raw hex with extra steps. The tint token exists for exactly this.
       */
      className="rounded-md border border-rust/30 bg-tint-rust px-3.5 py-2.5 text-[13px] text-rust"
    >
      {children}
    </p>
  );
}

/** The card every auth step sits in, so the steps don't jump around as you move between them. */
export function AuthCard({
  brand,
  title,
  intro,
  children,
}: {
  brand: string;
  title: string;
  intro?: ReactNode;
  children: ReactNode;
}) {
  return (
    <main className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-[26rem]">
        {/*
          THE ONE PLACE THE MARK IS LARGE — ~96px, chalk on a deep panel, feather rule fully
          visible. It is the only screen with nothing to do on it but arrive, so it is the only
          screen that can spend the room. Everywhere else the bird is a 44–54px lockup.

          The panel is navy for the same reason: this is the handover between the public site and
          the app, and the navy is what both halves have in common.
        */}
        {/*
          ONE CARD, ONE RADIUS, AND NO SHADOW.

          This was two stacked divs at `rounded-t-lg` / `rounded-b-lg` — 8px, a corner the brand does
          not contain — with a stock Tailwind `shadow-sm` under a panel that is not going anywhere.
          `tokens.ts` calls `lifted` "the only shadow in the product", reserved for a dragged or
          focused element, so that lifting MEANS something on the seating board. Spending it here
          spent it on nothing.

          It is the shared `Card` now: one 5px corner family, the navy band clipped inside it by
          `overflow-hidden`, and `border-light` doing the separating that the shadow was doing badly.
        */}
        <Card className="overflow-hidden">
          <div className="flex flex-col items-center gap-2 bg-deep px-6 pt-8 pb-7 text-center">
            <CountBird size={96} ground="navy" />
            <h1 className="font-display text-3xl leading-tight text-on-deep">{brand}</h1>
          </div>

          <div className="p-7">
            <h2 className="font-display text-xl leading-tight">{title}</h2>
            {intro ? <div className="mt-1.5 text-[13px] text-ink-2">{intro}</div> : null}
            <div className="mt-6">{children}</div>
          </div>
        </Card>

        {/*
          One band beneath the form — UI-UX-SPEC §2 lists sign-in as a yes. The drawn tile is the
          only moving part of the identity and this is a screen where something genuinely is in
          progress: you are part-way in.

          The 19-stack wall rather than the full 152, because this column is 26rem wide and the
          whole set at that width is a grey stripe.
        */}
        <Wall length={19} ground="light" drawnStack={11} className="mt-8 w-full" />
      </div>
    </main>
  );
}
